import { useCallback, useEffect, useState } from 'react'

/**
 * Lightweight controlled/uncontrolled dialog helper.
 *  - If `open` prop is provided the hook mirrors it (controlled).
 *  - If `open` is undefined the hook manages local state (uncontrolled).
 *
 * Params:
 *  - open?: boolean - external open state
 *  - onOpenChange?: (open: boolean) => void - callback when open state changes
 *
 * Usage:
 *  const { isOpen, handleOpenChange, handleClose } = useOpenCustomActionDialog(open, setOpen)
 */
export const useOpenCustomActionDialog = (open?: boolean, onOpenChange?: (open: boolean) => void) => {
  const [isOpen, setIsOpen] = useState(open ?? false)

  useEffect(() => {
    if (typeof open !== 'undefined') {
      setIsOpen(open)
    }
  }, [open])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (typeof open === 'undefined') {
        setIsOpen(newOpen)
      }
      onOpenChange?.(newOpen)
    },
    [open, onOpenChange],
  )
  const handleClose = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  return { isOpen, handleOpenChange, handleClose }
}
